/**
 * ============================================================================
 * SupplierRow — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: render a single supplier <tr> in the workbench table. Includes the
 *          criticality chip, lifecycle status chip, risk score with category
 *          colour-coding, and the next-requalification date.
 *
 * A11Y: chips carry both a coloured dot and a textual label so colour is not
 *  the sole signal (WCAG 1.4.1). Row actions are real buttons with visible
 *  text labels.
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import styles from './SupplierWorkbench.module.css';
import type {
  RiskCategory,
  SupplierCriticality,
  SupplierLifecycleStatus,
  SupplierSummary,
} from './useSuppliersQuery';

const CRIT_CLASS: Record<SupplierCriticality, string> = {
  CRITICAL: styles.critCritical,
  MAJOR: styles.critMajor,
  MINOR: styles.critMinor,
  LOW: styles.critLow,
};

const LIFE_CLASS: Record<SupplierLifecycleStatus, string> = {
  PENDING_REQUEST: styles.lifePending,
  QUESTIONNAIRE_ISSUED: styles.lifePending,
  AUDIT_SCHEDULED: styles.lifePending,
  ACTIVE: styles.lifeActive,
  RESTRICTED: styles.lifeRestricted,
  DISQUALIFIED: styles.lifeDisqualified,
  ARCHIVED: styles.lifePending,
};

const RISK_DOT: Record<RiskCategory, string> = {
  CRITICAL: styles.dotCritical,
  MAJOR: styles.dotWarn,
  MINOR: styles.dotInfo,
  LOW: styles.dotNeutral,
};

export interface SupplierRowProps {
  supplier: SupplierSummary;
  onOpen: (s: SupplierSummary) => void;
}

export function SupplierRow({ supplier: s, onOpen }: SupplierRowProps) {
  const { t, i18n } = useTranslation();
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  return (
    <tr>
      <td>
        <button
          type="button"
          className={styles.linkBtn}
          onClick={() => onOpen(s)}
          aria-label={t('supplier.workbench.openAria', {
            defaultValue: 'Open supplier {{name}}',
            name: s.name,
          })}
        >
          <strong>{s.name}</strong>
        </button>
        {s.legal_name && s.legal_name !== s.name && (
          <div className={styles.subdued}>{s.legal_name}</div>
        )}
      </td>
      <td>
        <span
          className={`${styles.chip} ${CRIT_CLASS[s.criticality]}`}
          aria-label={t('supplier.criticality.aria', {
            defaultValue: 'Criticality: {{level}}',
            level: t(`supplier.criticality.${s.criticality}`, {
              defaultValue: s.criticality,
            }),
          })}
        >
          {t(`supplier.criticality.${s.criticality}`, {
            defaultValue: s.criticality,
          })}
        </span>
      </td>
      <td>
        {t(`supplier.subtype.${s.subtype}`, { defaultValue: s.subtype })}
      </td>
      <td>
        <span
          className={`${styles.chip} ${LIFE_CLASS[s.lifecycle_status]}`}
          aria-label={t('supplier.lifecycle.aria', {
            defaultValue: 'Lifecycle status: {{status}}',
            status: t(`supplier.lifecycle.${s.lifecycle_status}`, {
              defaultValue: s.lifecycle_status,
            }),
          })}
        >
          {t(`supplier.lifecycle.${s.lifecycle_status}`, {
            defaultValue: s.lifecycle_status,
          })}
        </span>
      </td>
      <td>
        {s.risk_score == null ? (
          <span className={styles.subdued} aria-label={t('supplier.risk.noScore', { defaultValue: 'No risk score yet' })}>
            —
          </span>
        ) : (
          <div className={styles.riskCell}>
            <span
              className={`${styles.dot} ${s.risk_category ? RISK_DOT[s.risk_category] : styles.dotNeutral}`}
              aria-hidden="true"
            />
            <span className={styles.riskNumber}>{nf.format(s.risk_score)}</span>
            {s.risk_category && (
              <span className={styles.riskCatText}>
                {t(`supplier.risk.${s.risk_category}`, {
                  defaultValue: s.risk_category,
                })}
              </span>
            )}
          </div>
        )}
      </td>
      <td>
        {s.next_requalification_at_utc ? (
          <time dateTime={s.next_requalification_at_utc}>
            {dateFmt.format(new Date(s.next_requalification_at_utc))}
          </time>
        ) : (
          <span className={styles.subdued}>—</span>
        )}
      </td>
      <td>
        <Button
          size="sm"
          variant="tertiary"
          onClick={() => onOpen(s)}
          aria-label={t('supplier.workbench.viewAria', {
            defaultValue: 'View details for supplier {{name}}',
            name: s.name,
          })}
        >
          {t('common.view', { defaultValue: 'View' })}
        </Button>
      </td>
    </tr>
  );
}
